import {
    loadVision,
    loadModel,
    initTracker,
    track,
    destroyTracker,
    startPositioning,
    initAgeEstimator,
    estimateAge,
    createSession as createLivenessSession,
    processFrame,
    isComplete,
    isPassed,
    currentInstruction,
    currentTaskId,
    progress,
    decide,
} from './detect.js';
import {
    createTransport,
    initVM,
    setFaceData,
    setChallengeParams,
    executeChallenge as execVM,
    registerBridge,
    unregisterBridge,
    destroyVM,
    toBase64,
    ensureModels,
    getMediaPipeModelBuffer,
    clearModelCache,
} from './net.js';
import {
    AGE_SAMPLE_FRAMES,
    AGE_SAMPLE_INTERVAL_MS,
    AGE_FRAME_TIMEOUT_MS,
    ERROR_STEP_SECONDS,
    MOTION_CAPTURE_MS,
    MOTION_SAMPLE_MS,
} from './constants.js';

export { decide };

/* ── Camera + permission ───────────────────────── */

const CAMERA_CONSTRAINTS = {
    video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
};

const CAMERA_MESSAGES = {
    denied: 'Camera access was blocked. Allow camera access and try again.',
    missing: 'No camera available. Plug in a camera and try again.',
    busy: 'Camera is in use by another app. Close it and try again.',
    failed: 'Could not start the camera. Please try again.',
};

let stream = null;
let videoElement = null;

export class CameraError extends Error {
    constructor(reason, original) {
        super(reason);
        this.name = 'CameraError';
        this.reason = reason;
        this.original = original || null;
    }
}

function classifyCamera(error) {
    const name = error?.name || '';
    const text = `${name} ${error?.message || ''}`.toLowerCase();

    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'denied';
    if (/permission|denied|disallowed/.test(text)) return 'denied';
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'missing';
    if (/not found|no camera|requested device/.test(text)) return 'missing';
    if (name === 'NotReadableError' || name === 'TrackStartError') return 'busy';
    if (/could not start|in use|already/.test(text)) return 'busy';
    return 'failed';
}

function cameraMessage(error) {
    if (error instanceof CameraError)
        return CAMERA_MESSAGES[error.reason] || CAMERA_MESSAGES.failed;
    return CAMERA_MESSAGES.failed;
}

function waitForVideoReady(video) {
    return new Promise((resolve) => {
        const finish = () => {
            video.onloadedmetadata = null;
            video.play()?.catch(() => {});
            resolve({ width: video.videoWidth, height: video.videoHeight });
        };

        if (video.readyState >= 1 && video.videoWidth > 0) return finish();
        video.onloadedmetadata = finish;
    });
}

export async function startCamera(video) {
    stopCamera();

    let acquired;
    try {
        acquired = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
    } catch (error) {
        throw new CameraError(classifyCamera(error), error);
    }

    stream = acquired;
    videoElement = video;
    video.srcObject = stream;
    return waitForVideoReady(video);
}

export function stopCamera() {
    if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
    }
    if (videoElement) {
        videoElement.onloadedmetadata = null;
        videoElement.srcObject = null;
        videoElement = null;
    }
}

export function isCameraActive() {
    return stream !== null && videoElement !== null;
}

export function captureFrame() {
    if (!videoElement || !videoElement.videoWidth) return null;

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.getContext('2d').drawImage(videoElement, 0, 0);
    return canvas;
}

export async function getPermissionState() {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown';
    try {
        return (await navigator.permissions.query({ name: 'camera' })).state;
    } catch {
        return 'unknown';
    }
}

const TASK_LABELS = {
    'turn-left': 'Turn your head left',
    'turn-right': 'Turn your head right',
    nod: 'Nod your head',
    'blink-twice': 'Blink twice',
    'move-closer': 'Move closer then back',
};

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout(promise, ms) {
    return new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };
        const timer = setTimeout(() => finish(null), ms);
        Promise.resolve(promise)
            .then((value) => {
                clearTimeout(timer);
                finish(value);
            })
            .catch(() => {
                clearTimeout(timer);
                finish(null);
            });
    });
}

function isInline(widget) {
    return widget?.params?.layout === 'inline';
}

function errorMessage(error) {
    if (error instanceof CameraError) return cameraMessage(error);
    if (/positioning timeout/i.test(error?.message || '')) {
        return 'Face positioning timed out. Please try again.';
    }
    return 'Verification failed. Please try again.';
}

export async function runChallenge(widget, emitter) {
    const mode = widget.params.mode || 'serverless';

    if (mode !== 'serverless' && !widget.params.sitekey) {
        const error = new Error('Configuration error');
        widget.showResult('fail', 'Verification failed');
        emitter.emit('error', error, widget.id);
        widget.params.errorCallback?.(error);
        return;
    }

    if (mode === 'serverless') return runServerless(widget, emitter);
    return runServer(widget, emitter);
}

async function runServerless(widget, emitter) {
    try {
        widget.openPopup();
        widget.setHeroStatus('Loading…');

        await Promise.all([loadVision(), initAgeEstimator()]);
        const modelBuffer = await loadModel();

        const transport = createTransport('serverless', widget.params);
        const result = await runOnce(widget, modelBuffer, transport);

        cleanupLocal();
        emitResult(widget, emitter, result);
    } catch (error) {
        cleanupLocal();
        await handleError(widget, emitter, error);
    }
}

async function runOnce(widget, modelBuffer, transport) {
    const video = await prepareCamera(widget, modelBuffer);

    widget.showLiveness();
    widget.setInstruction('');
    widget.setVideoStatus('Verifying…');

    const session = createLivenessSession();
    const ageSamples = [];
    await runLiveness(video, session, widget, ageSamples);

    if (session.failed || !isPassed(session)) {
        return { outcome: 'retry', reason: 'liveness' };
    }

    widget.setInstruction('Hold still…');
    widget.setVideoStatus('Estimating age…');
    await collectAgeSamples(ageSamples);

    const decision = decide(ageSamples, widget.params.minAge ?? 18);

    if (decision.outcome !== 'pass') {
        return {
            outcome: decision.outcome,
            estimatedAge: decision.estimatedAge,
            reason: decision.reason,
        };
    }

    const verified = await transport.verify({
        estimatedAge: decision.estimatedAge,
        livenessOk: true,
    });

    return {
        outcome: verified.token ? 'pass' : 'fail',
        token: verified.token,
        estimatedAge: decision.estimatedAge,
    };
}

async function prepareCamera(widget, modelBuffer) {
    const video = widget.showCamera();

    widget.setVideoStatus('Requesting camera…');
    await startCamera(video);

    widget.setVideoStatus('Preparing…');
    await initTracker(modelBuffer);

    widget.setVideoStatus('Position your face');
    await waitForPositioning(video, widget);

    return video;
}

async function runLiveness(video, session, widget, ageSamples) {
    let nextAgeAt = 0;

    return new Promise((resolve) => {
        const loop = async () => {
            const now = performance.now();
            const tracking = track(video, now);
            if (tracking) processFrame(session, tracking);

            if (tracking?.faceCount === 1 && now >= nextAgeAt) {
                nextAgeAt = now + AGE_SAMPLE_INTERVAL_MS;
                sampleAge(ageSamples);
            }

            widget.setInstruction(currentInstruction(session) || 'Done');
            widget.setTask(currentTaskId(session));
            widget.setProgress(progress(session));
            widget.setVideoStatus(
                `Check ${Math.min(session.currentIndex + 1, session.tasks.length)}` +
                    ` of ${session.tasks.length}`
            );

            if (session.failed || isComplete(session)) {
                resolve();
                return;
            }

            requestAnimationFrame(loop);
        };

        requestAnimationFrame(loop);
    });
}

function sampleAge(ageSamples) {
    const frame = captureFrame();
    if (!frame) return;
    withTimeout(estimateAge(frame), AGE_FRAME_TIMEOUT_MS).then((result) => {
        if (result) ageSamples.push(result);
    });
}

async function collectAgeSamples(ageSamples) {
    for (let i = 0; i < AGE_SAMPLE_FRAMES; i++) {
        const frame = captureFrame();
        if (frame) {
            const result = await withTimeout(estimateAge(frame), AGE_FRAME_TIMEOUT_MS);
            if (result) ageSamples.push(result);
        }
        if (i < AGE_SAMPLE_FRAMES - 1) await sleep(AGE_SAMPLE_INTERVAL_MS);
    }
}

function waitForPositioning(video, widget) {
    return new Promise((resolve, reject) => {
        const handle = startPositioning(video, {
            onStatus: (text) => widget.setVideoStatus(text),
            onReady: () => {
                clearTimeout(timer);
                resolve();
            },
        });

        const timer = setTimeout(() => {
            handle.cancel();
            reject(new Error('Positioning timeout'));
        }, 30000);
    });
}

const RETRY_MESSAGES = {
    liveness: 'Could not confirm the movements. Try again.',
    insufficient: 'Could not read your face clearly. Allow image access and try again.',
    unstable: 'The reading was unstable. Hold still in good light and try again.',
    borderline: 'Could not confirm your age. Try again.',
};

function retryMessage(reason) {
    return RETRY_MESSAGES[reason] || 'Could not confirm your age. Try again.';
}

function emitResult(widget, emitter, result) {
    const params = widget.params;
    const debugAge =
        params.debug && result.estimatedAge != null
            ? ` (age ${result.estimatedAge.toFixed(1)}, min ${params.minAge ?? 18})`
            : '';

    if (result.outcome === 'pass') {
        widget.token = result.token || null;
        widget.showResult('pass', `Verified${debugAge}`);
        emitter.emit('verified', result.token, widget.id);
        params.callback?.(result.token);
        return;
    }

    if (result.outcome === 'retry') {
        widget.showResult('retry', retryMessage(result.reason) + debugAge);
        return;
    }

    widget.showResult('fail', `Verification failed${debugAge}`);
    emitter.emit('error', 'failed', widget.id);
    params.errorCallback?.('failed');
}

async function handleError(widget, emitter, error) {
    const message = errorMessage(error);
    const emitted = error instanceof CameraError ? error.original || error : error;

    if (isInline(widget)) {
        widget.showResult('retry', message);
    } else {
        if (!widget.popup) widget.openPopup();
        widget.showError(message);
        for (let seconds = ERROR_STEP_SECONDS; seconds > 0 && widget.popup; seconds--) {
            widget.setErrorCountdown(seconds);
            await sleep(1000);
        }
        widget.closePopup();
        widget.setState('retry');
    }

    emitter.emit('error', emitted, widget.id);
    widget.params.errorCallback?.(emitted);
}

function cleanupLocal() {
    stopCamera();
    destroyTracker();
}

async function runServer(widget, emitter) {
    const params = widget.params;
    let transport;

    try {
        widget.openPopup();
        widget.setHeroStatus('Connecting…');

        transport = createTransport(params.mode, params);
        const session = await transport.createSession();

        widget.setHeroStatus('Loading…');
        await initVM(session);

        widget.setHeroStatus('Preparing…');
        await ensureModels(session, () => {});

        await loadVision();
        const buffer = await getMediaPipeModelBuffer(session);
        await initTracker(buffer);

        registerBridge({
            trackFace: () => {
                const video = widget.popupElements?.video;
                if (!video) return 'null';
                const result = track(video, performance.now());
                if (!result) return 'null';
                return JSON.stringify({
                    ts: result.timestampMs ?? performance.now(),
                    faceCount: result.faceCount,
                    headPose: result.headPose || null,
                    blendshapes: result.blendshapes || null,
                    boundingBox: result.boundingBox || null,
                });
            },
            captureFrame: () => (captureFrame() ? 'true' : 'null'),
        });

        const video = widget.showCamera();
        widget.setVideoStatus('Requesting camera…');
        await startCamera(video);
        exposeMirrorVideo(video);

        widget.setVideoStatus('Position your face');
        await waitForPositioning(video, widget);

        widget.showLiveness();
        transport.openChannel();
        await runServerRounds(widget, emitter, transport, session);
    } catch (error) {
        cleanupVM(transport);
        await handleError(widget, emitter, error);
    }
}

async function runServerRounds(widget, emitter, transport, session) {
    let challenge = await transport.receive();
    const rounds = session.rounds;

    for (let i = 0; i < rounds; i++) {
        if (!challenge || challenge.type === 'timeout') {
            return failServer(widget, emitter, transport);
        }

        if (challenge.type === 'verdict') {
            cleanupVM(transport);
            return emitVerdict(widget, emitter, challenge);
        }

        widget.setVideoStatus(`Step ${i + 1} of ${rounds}`);
        const task = challenge.token?.task;
        widget.setInstruction(TASK_LABELS[task] ?? 'Look at the camera');
        widget.setTask(task);
        widget.setProgress(i / rounds);

        setFaceData(await captureMotion(widget));
        setChallengeParams(challenge.token);

        let vmOutput;
        try {
            vmOutput = execVM();
        } catch {
            return failServer(widget, emitter, transport);
        }

        const result = await transport.sendAndReceive({
            token: challenge.token,
            tokenSignature: challenge.tokenSignature,
            response: toBase64(vmOutput),
        });

        if (!result) return failServer(widget, emitter, transport);

        if (result.complete) {
            cleanupVM(transport);
            return emitVerdict(widget, emitter, result);
        }

        if (result.hint) {
            widget.setVideoStatus(result.hint);
            await sleep(1000);
        }

        challenge = result.nextChallenge || null;
    }

    cleanupVM(transport);
}

function failServer(widget, emitter, transport) {
    cleanupVM(transport);
    widget.showResult('fail', 'Verification failed');
    emitter.emit('error', 'failed', widget.id);
}

function emitVerdict(widget, emitter, response) {
    const verdict = response?.verdict || response;
    const token = verdict?.token || null;
    const params = widget.params;

    if (token) {
        widget.token = token;
        widget.showResult('pass', 'Verified');
        emitter.emit('verified', token, widget.id);
        params.callback?.(token);
        return;
    }

    widget.showResult('fail', 'Verification failed');
    emitter.emit('error', 'failed', widget.id);
    params.errorCallback?.('failed');
}

async function captureMotion(widget) {
    const video = widget.popupElements?.video;
    const history = [];
    const start = performance.now();

    while (performance.now() - start < MOTION_CAPTURE_MS) {
        const result = track(video, performance.now());
        if (result && result.faceCount === 1) {
            history.push({
                ts: result.timestampMs,
                headPose: result.headPose,
                blendshapes: result.blendshapes,
                boundingBox: result.boundingBox,
            });
        }
        await sleep(MOTION_SAMPLE_MS);
    }

    return { faceCount: history.length > 0 ? 1 : 0, motionHistory: history };
}

function cleanupVM(transport) {
    stopCamera();
    removeMirrorVideo();
    destroyTracker();
    unregisterBridge();
    destroyVM();
    clearModelCache();
    transport?.close();
}

function exposeMirrorVideo(source) {
    removeMirrorVideo();
    if (!source?.srcObject) return;
    const mirror = document.createElement('video');
    mirror.id = '__openage_mirror';
    mirror.srcObject = source.srcObject;
    mirror.autoplay = true;
    mirror.muted = true;
    mirror.playsInline = true;
    mirror.style.cssText =
        'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1;';
    document.body.appendChild(mirror);
}

function removeMirrorVideo() {
    document.getElementById('__openage_mirror')?.remove();
}
